/**
 * Modelo de dominio: Alerta.
 *
 * Traduce lo que guarda DynamoDB —escrito por ProcessTelemetry— a la forma que
 * consumen las pantallas. Los dos vocabularios no coinciden y hasta ahora nadie
 * los unía: la interfaz esperaba `{id, type, title, message, timestamp}` con
 * `type` entre cuatro iconos, mientras el backend escribe `{alert_id,
 * alert_type, severity, message, value, recommendation}` con siete tipos.
 * La conversión vive aquí y no repartida por los componentes.
 */

/** Tipos que sabe dibujar `AlertNotification`. Cualquier otro cae en `info`. */
export const ALERT_ICON = {
  WARNING: 'warning',
  WATER: 'water',
  TIME: 'time',
  INFO: 'info',
};

/**
 * Cada tipo que genera ProcessTelemetry, con el icono y el título que le
 * corresponden. El título importa: el backend sólo manda `message`, y una lista
 * de mensajes sin encabezado se lee mucho peor.
 */
const CATALOGO = {
  continuous_flow:   { icon: ALERT_ICON.TIME,    title: 'Flujo continuo prolongado' },
  daily_excess:      { icon: ALERT_ICON.WATER,   title: 'Consumo diario excedido' },
  high_hourly_rate:  { icon: ALERT_ICON.WATER,   title: 'Caudal elevado' },
  leak_detection:    { icon: ALERT_ICON.WARNING, title: 'Posible fuga detectada' },
  pipe_burst:        { icon: ALERT_ICON.WARNING, title: 'Posible rotura de tubería' },
  weak_wifi_signal:  { icon: ALERT_ICON.INFO,    title: 'Señal WiFi débil' },
  low_memory:        { icon: ALERT_ICON.INFO,    title: 'Memoria baja en el dispositivo' },
};

/** Una alerta crítica se dibuja como advertencia aunque su tipo sea informativo. */
function iconoDe(alertType, severity) {
  if (severity === 'critical') return ALERT_ICON.WARNING;
  return CATALOGO[alertType]?.icon ?? ALERT_ICON.INFO;
}

function tituloDe(alertType) {
  return CATALOGO[alertType]?.title ?? 'Alerta del dispositivo';
}

/** Antigüedad en lenguaje natural, con el mismo tono que el resto de la app. */
export function formatAlertTime(timestampSegundos, nowSeconds = Date.now() / 1000) {
  const t = Number(timestampSegundos);
  if (!Number.isFinite(t) || t <= 0) return 'Sin fecha';

  const diff = Math.max(0, nowSeconds - (t > 1e12 ? t / 1000 : t));
  if (diff < 60) return 'Hace un momento';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
  return new Date((t > 1e12 ? t : t * 1000)).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
  });
}

/** Umbral de "nueva": 24 h. Es lo que separa las dos secciones de la pantalla. */
const NUEVA_MAX_AGE = 86400;

export function normalizeAlert(raw, nowSeconds = Date.now() / 1000) {
  // El mock ya entrega la forma de la interfaz; el backend, la suya.
  const alertType = raw.alert_type ?? raw.alertType ?? raw.type;
  const t = Number(raw.timestamp ?? 0);
  const segundos = t > 1e12 ? Math.floor(t / 1000) : Math.floor(t);

  return {
    id: raw.alert_id ?? raw.alertId ?? raw.id ?? `${raw.device_id ?? 'x'}-${segundos}`,
    deviceId: raw.device_id ?? raw.deviceId ?? null,
    type: iconoDe(alertType, raw.severity),
    alertType: alertType ?? null,
    severity: raw.severity ?? 'info',
    title: raw.title ?? tituloDe(alertType),
    message: raw.message ?? '',
    recommendation: raw.recommendation ?? '',
    timestampSeconds: segundos,
    timestamp: raw.timestamp && typeof raw.timestamp === 'string'
      ? raw.timestamp                       // el mock ya la da legible
      : formatAlertTime(segundos, nowSeconds),
    isNew: raw.isNew ?? (segundos > 0 && nowSeconds - segundos <= NUEVA_MAX_AGE),
    resolved: Boolean(raw.resolved),
  };
}

/** Ordena de más reciente a más antigua; las del mock conservan su orden. */
export function sortAlerts(alertas = []) {
  return [...alertas].sort((a, b) => (b.timestampSeconds ?? 0) - (a.timestampSeconds ?? 0));
}
